import styled from 'styled-components';
import { Typography } from '../../../../components/display/Typography';

// ═══════════════════════════════════════════════════════════════════
// CONTAINER - Siguiendo patrón de ShowOT
// ═══════════════════════════════════════════════════════════════════

/* `min-width: 0` no es adorno: sin él la tabla de Tramos se volvía
   INALCANZABLE. Esta página es un item flex a la derecha del menú del módulo
   (NavContainer, 360px fijos + gap). Un item flex trae `min-width: auto`, o
   sea que se niega a encogerse por debajo del ancho de su contenido: con 13
   columnas la tabla pedía 1.736px donde había 1.425, y el desbordamiento subía
   hasta un contenedor del layout global que lo recorta con `overflow-x: clip`
   — y `clip`, a diferencia de `auto`, NO deja hacer scroll. Resultado: las dos
   últimas columnas («OT sala» y «Corregir») quedaban cortadas sin ninguna
   forma de llegar a ellas, justo el botón que estrena la corrección de tramos.
   Con `min-width: 0` el item sí encoge y toma el relevo el `overflow-x: auto`
   que la propia tabla ya tenía. */
export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: 0;
`;

// ═══════════════════════════════════════════════════════════════════
// CONTENT AREA - Layout 2 columnas como ActionContentCardResume
// ═══════════════════════════════════════════════════════════════════

/* Disc#12 (fix 2026-07-03): SIN padding propio — el layout global ya da los
   gaps y las páginas canónicas (Communications) cuelgan el contenido a ancho
   completo. El padding xl aquí metía un marco de 4rem alrededor del detalle. */
export const ContentArea = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  height: 100%;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    gap: ${({ theme }) => theme.spacing.l};
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.m};
  }
`;

/* Task 3 (2026-07-07): sin `SidePanel` — el panel lateral `RouteControlPanel`
   murió (FR-6.5/6.6, cabecera `RouteHeaderCard` arriba del flujo). `MainContent`
   es ahora el único hijo de `ContentArea`, ocupa el ancho completo. */
export const MainContent = styled.div`
  flex: 1;
  /* Mismo motivo que en PageContainer: sin esto el flex no encoge por debajo
     del ancho de la tabla de Tramos y el desbordamiento acaba recortado más
     arriba. */
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
  overflow-y: auto;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    gap: ${({ theme }) => theme.spacing.l};
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    gap: ${({ theme }) => theme.spacing.m};
  }
`;

// ═══════════════════════════════════════════════════════════════════
// SECTIONS - Usando Typography del proyecto
// ═══════════════════════════════════════════════════════════════════

export const SectionTitle = styled(Typography)`
  font-size: ${({ theme }) => theme.fontSize.L};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  margin: 0;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.M};
  }
`;

// ═══════════════════════════════════════════════════════════════════
// SECTION CARD — patrón ContainerOT de ShowOT/MainStyledComponents.ts
// ═══════════════════════════════════════════════════════════════════

export const SectionCard = styled.div`
  /* Ancho completo, alineado con la cabecera y el mapa (feedback usuario
     2026-07-07 — el max-width 89% dejaba la card de Tramos descolgada). */
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
  background-color: ${({ theme }) => theme.colors.backgroundLila};
  border-radius: 0.8rem;
  padding: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    padding: ${({ theme }) => theme.spacing.l};
  }
`;

/* `ActionButtons` eliminado 2026-07-17 junto al botón "Crear incidència"
   (su único consumidor) — ver RoutePreviewPage. */

/* `DirectionChangeNotice` y `DirectionChangeActions` eliminados 2026-09-01: el
   aviso de cambio de sentido pasó a toast cuando el detector se mudó al
   `TimerOverlay` — ya no hay página donde pintarlo. */

/* Aviso «fuera del trazado» + su botón «Añadir» (2026-09-23). Una fila propia:
   `MainContent` es una columna flex que ESTIRA a sus hijos, y el botón suelto
   se pintaba a todo el ancho con el texto a la izquierda. Con `flex-wrap`, en
   una tablet vertical el botón baja debajo del texto en vez de apretarlo. */
export const DirectionBlindRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.m};
`;

/**
 * Cabecera de la card de Tramos: el título y, para gestión, «Mover tramos»
 * (plan 2026-10-15 § 3) a la derecha. Con `flex-wrap`, en una tablet vertical
 * el botón baja debajo del título en vez de apretarlo.
 */
export const SectionHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.m};
`;

/**
 * Lado derecho de la cabecera de Tramos: el progreso de «revisados», «Siguiente
 * sin revisar» y «Mover tramos». Con `flex-wrap`, en una tablet vertical baja
 * entero debajo del título y, si no cabe, parte en dos filas.
 */
export const SectionHeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.m};
`;

/** «Revisados N de M» (el color lo pone la página: verde oscuro si no falta ninguno). */
export const ReviewProgress = styled(Typography)`
  margin: 0;
`;
